import React from "react";
import { useAppSelector } from "../../../redux/store";
import GamePage from "../game-page/game-page";

import { Story } from "../../../types";
import { format } from "date-fns";
import { now } from "../../../time";
import { saveFile, saveTextFile } from "../../../utils/fileSaver";
import { storyToText } from "./story-results";
import { fileNameTxt } from "./story-view";
import CanvasViewSolo from "../../common/canvas-view/canvas-view-solo";
import { SafeJoin } from "../../../util";
import { useTranslation } from "react-i18next";
import PrevSubmission from "../../common/prev-submission/prev-submission";
import { constants } from "../../../constants";
import Button from "@/components/ui/button";

type ResultsProps = {
  onGameStats: () => void;
};

const SoloLeaderboard = ({ onGameStats }: ResultsProps) => {
  const gameState = useAppSelector((state) => state.game);

  const accessibility = useAppSelector(
    (state) => state.player.player?.meta.accessibility2
  );

  const story: Story = gameState.game?.stories
    ? gameState.game?.stories[0]
    : ({} as Story);

  const { t } = useTranslation("game");


  function playAgain() {
    window.open("https://franticfanfic.com", "_self");
  }

  function fileNameImage(story: Story) {
    return `FranticFanfic_${story.meta.title}_${format(now(), "LLL_dd_yyyy")}.png`;
  }

  async function takeScreenshot(e: React.MouseEvent<HTMLButtonElement>) {
    const { default: html2canvas } = await import("html2canvas");
    const canvasId = `canvas-${story.id}`;
    const canvasEl = document.querySelector(`#${canvasId}`) as HTMLElement;
    if (!canvasEl) return;

    const canvas = await html2canvas(canvasEl, { scrollY: -window.scrollY });

    canvas.toBlob(async (blob) => {
      if (blob) {
        await saveFile(blob, fileNameImage(story));
      }
    }, "image/png");
  }

  const footer = (
    <>
      <Button className={"text-lg"} onClick={(_) => playAgain()}>
        {t("storyResults.footers.default")}
      </Button>
      <Button className={"text-sm flex-grow mx-1"} onClick={handleSupportUs}>
        {t("storyResults.footers.likeGame")}
        <br />
        {t("storyResults.footers.supportUs")}
      </Button>
      <Button className={"text-lg"} onClick={handleGameStats}>
        {t("storyResults.footers.gameStats")}
      </Button>
    </>
  );

  function handleSupportUs() {
    window.open("https://ko-fi.com/franticfanfic", "_blank");
  }

  function handleGameStats() {
    onGameStats();
  }

  function titlesJsx() {
    return [
      <span className={"font-bold underline"}>{story?.meta.title}</span>,
      <>
        characters:{" "}
        {SafeJoin(
          story?.characters &&
          story.characters
            .filter((c) => c.selected)
            .map<React.ReactNode>((c) => (
              <i key={"character-" + c.id}>{c.name}</i>
            )),
          ", "
        )}{" "}
      </>,
    ];
  }

  async function handleTextClick() {
    let text = storyToText(story);
    await saveTextFile(text, fileNameTxt(story));
  }

  function subheaders() {
    if (!gameState.game?.settings.solo_writing) {
      return [
        t("storyResults.subheaders.react"),
        t("storyResults.subheaders.click"),
        t("storyResults.subheaders.download"),
      ];
    } else {
      return [t("storyResults.subheaders.downloadSolo")];
    }
  }

  return (
    <GamePage
      footerJsx={footer}
      titlesJsx={titlesJsx()}
      subHeaders={subheaders()}
      hack={gameState.game?.stories?.map((s) => (
        <CanvasViewSolo
          multiPrompt={gameState.game?.settings.multi_prompt}
          key={"canvas-" + s.id}
          story={s}
        />
      ))}
    >
      <div className="w-full flex-grow flex flex-col divide-y divide-gray-200">
        {story?.submissions.map<React.ReactNode>((s, idx) => {
          return (
            <div
              className="p-4 text-lg break-words whitespace-pre-wrap normal-case"
              key={"story-" + s.id}
              style={constants.accessibleStyle(accessibility)}
            >
              <PrevSubmission variant="read" submission={s} />
              {gameState.game?.settings.multi_prompt ||
                s.index === story.submissions.length - 1 ? (
                <div
                  className={"vote-row-buttons font-sans"}
                  style={{
                    fontStyle: "italic",
                    marginTop: "0.5rem",
                  }}
                >
                  prompt: {s.meta.prompt}
                </div>
              ) : (
                ""
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col border-t-[thick] border-t-black border-b-[thick] border-b-black">
        <div className="flex flex-row justify-around py-2">
          <Button
            className={"text-base sm:text-xl sm:mx-4"}
            onClick={takeScreenshot}
          >
            Download Image
          </Button>
          <Button
            className={"text-base sm:text-xl sm:mx-4"}
            onClick={handleTextClick}
          >
            Download Text
          </Button>
        </div>
      </div>
    </GamePage>
  );
};

export default SoloLeaderboard;
